/**
 * Stage Registry — 11 wide stages (4096px) with unique visual themes.
 *
 * All stages are pure sand terrain (no wireframe). Everything is destructible.
 * Each stage defines a color palette and references a layout generator.
 */

import type { WireframeLayout } from '../types';
import {
  generateFinalDestinationLayout, generateBattlefieldLayout, generateFountainLayout,
  generateStadiumLayout, generateSkybridgeLayout, generateTwinPeaksLayout,
  generateColosseumLayout, generateArchipelagoLayout, generateThePitLayout,
  generateCalderaLayout, generateStalactiteLayout,
} from './layouts';

// ── Theme Interface ──────────────────────────────────────────────

export interface StageTheme {
  // Identity
  id: string;
  name: string;
  subtitle: string;

  // Terrain fill colors (4-tier depth gradient, RGB arrays)
  sandSurface: readonly [number, number, number];
  sandStandard: readonly [number, number, number];
  sandMid: readonly [number, number, number];
  sandDeep: readonly [number, number, number];
  wireSurface: readonly [number, number, number];
  wireStandard: readonly [number, number, number];
  wireMid: readonly [number, number, number];
  wireDeep: readonly [number, number, number];
  sandEdgeColor: number;
  wireEdgeColor: number;

  // Void colors
  voidEdgeColor: number;
  voidDeepColor: number;
  voidDarkestColor: number;
  voidRiseFlashColor: number;

  // Background
  bgColor: number;
  starColors: number[];
  moonColors: number[];

  // Bumper line colors
  bumperColor: number;
  bumperBright: number;

  // Background grid visibility (only Neon Void keeps the grid)
  showBackgroundGrid: boolean;
}

export interface StageDefinition {
  theme: StageTheme;
  generateLayout: (stageLeft: number, stageWidth: number, playerCount: number) => WireframeLayout;
  worldWidth?: number;  // custom world width (default 2560) — wider stages use 4096+
  worldHeight?: number; // custom world height (default 1440) — taller stages use 1800+
}

// ── Base Themes (11 color palettes) ──────────────────────────────

const NEON_VOID_THEME: StageTheme = {
  id: 'neon_void', name: 'Neon Void', subtitle: 'The Proving Ground',
  sandSurface: [10, 12, 30], sandStandard: [5, 5, 16], sandMid: [3, 3, 12], sandDeep: [2, 2, 8],
  wireSurface: [8, 20, 35], wireStandard: [5, 14, 25], wireMid: [3, 10, 18], wireDeep: [2, 6, 12],
  sandEdgeColor: 0x00ffcc, wireEdgeColor: 0x4488ff,
  voidEdgeColor: 0xff00ff, voidDeepColor: 0x330055, voidDarkestColor: 0x1a0033, voidRiseFlashColor: 0x8800ff,
  bgColor: 0x050508, starColors: [0xffffff, 0xaaccff, 0xffddaa, 0xddddff], moonColors: [0x4455aa, 0x6644aa, 0x445588],
  bumperColor: 0x4488cc, bumperBright: 0x00ffff, showBackgroundGrid: false,
};

const HELLFIRE_THEME: StageTheme = {
  id: 'hellfire', name: 'Hellfire', subtitle: 'The Crucible',
  sandSurface: [15, 8, 8], sandStandard: [10, 5, 5], sandMid: [5, 2, 2], sandDeep: [3, 1, 1],
  wireSurface: [20, 10, 5], wireStandard: [14, 7, 3], wireMid: [10, 5, 2], wireDeep: [6, 3, 1],
  sandEdgeColor: 0xff4400, wireEdgeColor: 0xff6600,
  voidEdgeColor: 0xcc4400, voidDeepColor: 0x441005, voidDarkestColor: 0x200802, voidRiseFlashColor: 0xff6600,
  bgColor: 0x050002, starColors: [0xff6644, 0xff4422, 0xffaa44, 0xff8833], moonColors: [0x3a1005, 0x2a0505, 0x1a0000],
  bumperColor: 0x884422, bumperBright: 0xff4400, showBackgroundGrid: false,
};

const PAPER_THEATER_THEME: StageTheme = {
  id: 'paper_theater', name: 'Paper Theater', subtitle: 'Kamishibai',
  sandSurface: [8, 5, 3], sandStandard: [6, 4, 2], sandMid: [4, 3, 2], sandDeep: [3, 2, 1],
  wireSurface: [10, 7, 4], wireStandard: [7, 5, 3], wireMid: [5, 3, 2], wireDeep: [3, 2, 1],
  sandEdgeColor: 0xffd700, wireEdgeColor: 0xc4a06a,
  voidEdgeColor: 0xffd700, voidDeepColor: 0x0a1830, voidDarkestColor: 0x050c18, voidRiseFlashColor: 0xffaa44,
  bgColor: 0x1a1008, starColors: [0xffeedd, 0xffd4a0, 0xffe8c0, 0xfff0d0], moonColors: [0x8b6a3e, 0x6b4a28, 0x4a3018],
  bumperColor: 0x8b6a3e, bumperBright: 0xffd700, showBackgroundGrid: false,
};

const MOSS_CIRCUIT_THEME: StageTheme = {
  id: 'moss_circuit', name: 'Moss Circuit', subtitle: 'Overgrown Network',
  sandSurface: [5, 10, 5], sandStandard: [3, 7, 3], sandMid: [2, 5, 2], sandDeep: [1, 3, 1],
  wireSurface: [4, 12, 6], wireStandard: [3, 8, 4], wireMid: [2, 6, 3], wireDeep: [1, 4, 2],
  sandEdgeColor: 0x44aa44, wireEdgeColor: 0x66ffcc,
  voidEdgeColor: 0x44ff44, voidDeepColor: 0x0a2a0a, voidDarkestColor: 0x051505, voidRiseFlashColor: 0x44cc88,
  bgColor: 0x0a1a0a, starColors: [0x66ffcc, 0x44aa88, 0x88ffdd, 0xaaffee], moonColors: [0x1a3a1a, 0x0d200d, 0x142814],
  bumperColor: 0x336633, bumperBright: 0x44aa44, showBackgroundGrid: false,
};

const ARCHIPELAGO_THEME: StageTheme = {
  id: 'archipelago', name: 'Archipelago', subtitle: 'Scattered Isles',
  sandSurface: [8, 20, 30], sandStandard: [5, 14, 22], sandMid: [3, 10, 16], sandDeep: [2, 6, 10],
  wireSurface: [6, 25, 35], wireStandard: [4, 18, 28], wireMid: [2, 12, 20], wireDeep: [1, 8, 14],
  sandEdgeColor: 0x00ccaa, wireEdgeColor: 0x44ddff,
  voidEdgeColor: 0x0066aa, voidDeepColor: 0x001133, voidDarkestColor: 0x000811, voidRiseFlashColor: 0x0088cc,
  bgColor: 0x020810, starColors: [0x88ddff, 0x44bbee, 0xaaeeff, 0x66ccdd], moonColors: [0x0a2040, 0x061530, 0x041020],
  bumperColor: 0x226666, bumperBright: 0x00ccaa, showBackgroundGrid: false,
};

const THE_ARCH_THEME: StageTheme = {
  id: 'the_arch', name: 'The Arch', subtitle: "Titan's Gate",
  sandSurface: [8, 6, 3], sandStandard: [6, 4, 2], sandMid: [4, 3, 2], sandDeep: [3, 2, 1],
  wireSurface: [10, 7, 4], wireStandard: [7, 5, 3], wireMid: [5, 3, 2], wireDeep: [3, 2, 1],
  sandEdgeColor: 0xffbb44, wireEdgeColor: 0xffeedd,
  voidEdgeColor: 0x884422, voidDeepColor: 0x1a0a04, voidDarkestColor: 0x0a0502, voidRiseFlashColor: 0xcc6633,
  bgColor: 0x0f0804, starColors: [0xffeedd, 0xffcc88, 0xffe0b0, 0xffd4a0], moonColors: [0x3a2010, 0x2a1508, 0x1a0c04],
  bumperColor: 0x665533, bumperBright: 0xffbb44, showBackgroundGrid: false,
};

const STALACTITE_THEME: StageTheme = {
  id: 'stalactite', name: 'Stalactite Gauntlet', subtitle: 'Frostbite Cavern',
  sandSurface: [16, 22, 34], sandStandard: [10, 16, 26], sandMid: [6, 10, 18], sandDeep: [3, 6, 12],
  wireSurface: [20, 28, 40], wireStandard: [14, 20, 32], wireMid: [8, 14, 24], wireDeep: [4, 8, 16],
  sandEdgeColor: 0x88ccff, wireEdgeColor: 0xccddff,
  voidEdgeColor: 0x4488cc, voidDeepColor: 0x081828, voidDarkestColor: 0x040c14, voidRiseFlashColor: 0x66aaee,
  bgColor: 0x040810, starColors: [0xccddff, 0x88bbee, 0xaaccff, 0xddeeff], moonColors: [0x102030, 0x0a1820, 0x061014],
  bumperColor: 0x446688, bumperBright: 0x88ccff, showBackgroundGrid: false,
};

const COLOSSEUM_THEME: StageTheme = {
  id: 'colosseum', name: 'The Colosseum', subtitle: 'Arena of Ruin',
  sandSurface: [6, 5, 4], sandStandard: [4, 4, 3], sandMid: [3, 3, 2], sandDeep: [2, 2, 1],
  wireSurface: [8, 6, 5], wireStandard: [6, 5, 4], wireMid: [4, 3, 2], wireDeep: [2, 2, 1],
  sandEdgeColor: 0xffe8aa, wireEdgeColor: 0xffd700,
  voidEdgeColor: 0x6622aa, voidDeepColor: 0x0c0418, voidDarkestColor: 0x06020c, voidRiseFlashColor: 0x8844cc,
  bgColor: 0x080410, starColors: [0xffe8cc, 0xffd4a0, 0xffeecc, 0xeeddbb], moonColors: [0x201030, 0x140a20, 0x0c0618],
  bumperColor: 0x665544, bumperBright: 0xffe8aa, showBackgroundGrid: false,
};

const TWIN_PILLARS_THEME: StageTheme = {
  id: 'twin_pillars', name: 'Twin Pillars', subtitle: 'The Divide',
  sandSurface: [20, 10, 30], sandStandard: [14, 7, 22], sandMid: [9, 4, 16], sandDeep: [5, 2, 10],
  wireSurface: [28, 14, 38], wireStandard: [20, 10, 28], wireMid: [14, 6, 20], wireDeep: [8, 3, 14],
  sandEdgeColor: 0xff66cc, wireEdgeColor: 0xcc88ff,
  voidEdgeColor: 0x6622cc, voidDeepColor: 0x0a0420, voidDarkestColor: 0x050210, voidRiseFlashColor: 0x8844ff,
  bgColor: 0x060212, starColors: [0xcc88ff, 0xff66cc, 0xaa66ee, 0xdd88ff], moonColors: [0x1a0830, 0x100420, 0x0a0218],
  bumperColor: 0x553388, bumperBright: 0xff66cc, showBackgroundGrid: false,
};

const HONEYCOMB_THEME: StageTheme = {
  id: 'honeycomb', name: 'Honeycomb', subtitle: 'The Hive',
  sandSurface: [6, 4, 2], sandStandard: [4, 3, 1], sandMid: [3, 2, 1], sandDeep: [2, 1, 1],
  wireSurface: [8, 5, 2], wireStandard: [5, 4, 2], wireMid: [4, 3, 1], wireDeep: [2, 2, 1],
  sandEdgeColor: 0xffaa22, wireEdgeColor: 0xffcc44,
  voidEdgeColor: 0xaa6600, voidDeepColor: 0x1a0c02, voidDarkestColor: 0x0a0601, voidRiseFlashColor: 0xcc8800,
  bgColor: 0x0c0804, starColors: [0xffcc44, 0xffaa22, 0xffdd66, 0xffbb33], moonColors: [0x2a1a08, 0x1a1004, 0x100a02],
  bumperColor: 0x886622, bumperBright: 0xffaa22, showBackgroundGrid: false,
};

const THE_SHELF_THEME: StageTheme = {
  id: 'the_shelf', name: 'The Shelf', subtitle: 'Last Stand',
  sandSurface: [18, 18, 24], sandStandard: [12, 12, 18], sandMid: [8, 8, 12], sandDeep: [4, 4, 8],
  wireSurface: [24, 24, 32], wireStandard: [18, 18, 24], wireMid: [12, 12, 18], wireDeep: [6, 6, 10],
  sandEdgeColor: 0xddddff, wireEdgeColor: 0xffffff,
  voidEdgeColor: 0xddddff, voidDeepColor: 0x0c1828, voidDarkestColor: 0x060c14, voidRiseFlashColor: 0x6688cc,
  bgColor: 0x030406, starColors: [0xffffff, 0xddddff, 0xaabbdd, 0xccccee], moonColors: [0x101420, 0x0a0e18, 0x060810],
  bumperColor: 0x445566, bumperBright: 0xddddff, showBackgroundGrid: false,
};

// ── Theme lookup by base ID ──────────────────────────────────────

export const BASE_THEMES: Record<string, StageTheme> = {
  neon_void: NEON_VOID_THEME,
  hellfire: HELLFIRE_THEME,
  paper_theater: PAPER_THEATER_THEME,
  moss_circuit: MOSS_CIRCUIT_THEME,
  archipelago: ARCHIPELAGO_THEME,
  the_arch: THE_ARCH_THEME,
  stalactite: STALACTITE_THEME,
  colosseum: COLOSSEUM_THEME,
  twin_pillars: TWIN_PILLARS_THEME,
  honeycomb: HONEYCOMB_THEME,
  the_shelf: THE_SHELF_THEME,
};

/** Get the base theme ID for a stage (used by background renderer). */
export function getBaseThemeId(stageId: string): string {
  const stage = STAGES[stageId];
  if (!stage) return 'neon_void';
  // Find which base theme this stage uses by matching the color palette
  for (const [baseId, baseTheme] of Object.entries(BASE_THEMES)) {
    if (stage.theme.sandEdgeColor === baseTheme.sandEdgeColor &&
        stage.theme.wireEdgeColor === baseTheme.wireEdgeColor) {
      return baseId;
    }
  }
  return 'neon_void';
}

// ── Wide Stage Definitions ───────────────────────────────────────
// All stages: 4096px wide, pure sand terrain, no wireframes.

function wideStage(
  baseThemeId: string,
  id: string,
  name: string,
  subtitle: string,
  generateLayout: (stageLeft: number, stageWidth: number, playerCount: number) => WireframeLayout,
  worldHeight?: number,
): StageDefinition {
  const baseTheme = BASE_THEMES[baseThemeId] ?? NEON_VOID_THEME;
  return {
    theme: { ...baseTheme, id, name, subtitle },
    generateLayout,
    worldWidth: 4096,
    ...(worldHeight ? { worldHeight } : {}),
  };
}

// Row 1 — Starter / Competitive
const FINAL_DESTINATION = wideStage('neon_void', 'final_destination', 'Final Destination', 'The Proving Ground', generateFinalDestinationLayout);
const BATTLEFIELD = wideStage('moss_circuit', 'battlefield', 'Battlefield', 'Sacred Ground', generateBattlefieldLayout);
const FOUNTAIN = wideStage('archipelago', 'fountain', 'Fountain', 'Rising Waters', generateFountainLayout);
const STADIUM = wideStage('colosseum', 'stadium', 'Stadium', 'The Arena', generateStadiumLayout);

// Row 2 — Technical / Varied
const SKYBRIDGE = wideStage('paper_theater', 'skybridge', 'Skybridge', 'The Crossing', generateSkybridgeLayout);
const TWIN_PEAKS = wideStage('twin_pillars', 'twin_peaks', 'Twin Peaks', 'The Divide', generateTwinPeaksLayout);
const COLOSSEUM = wideStage('the_arch', 'colosseum', 'Colosseum', 'Arena of Ruin', generateColosseumLayout);
const ARCHIPELAGO_STAGE = wideStage('the_shelf', 'archipelago_stage', 'Archipelago', 'Scattered Isles', generateArchipelagoLayout);

// Row 3 — Aggressive / Creative
const THE_PIT = wideStage('honeycomb', 'the_pit', 'The Pit', 'No Escape', generateThePitLayout);
const CALDERA = wideStage('hellfire', 'caldera', 'Caldera', 'Ring of Fire', generateCalderaLayout);
const STALACTITE = wideStage('stalactite', 'stalactite', 'Stalactite', 'Frostbite Cavern', generateStalactiteLayout, 1800);

// ── Registry ─────────────────────────────────────────────────────

const STAGES: Record<string, StageDefinition> = {
  // Base themes (kept for getStage fallback + custom levels)
  neon_void: { theme: NEON_VOID_THEME, generateLayout: generateFinalDestinationLayout },
  hellfire: { theme: HELLFIRE_THEME, generateLayout: generateCalderaLayout },
  paper_theater: { theme: PAPER_THEATER_THEME, generateLayout: generateSkybridgeLayout },
  moss_circuit: { theme: MOSS_CIRCUIT_THEME, generateLayout: generateBattlefieldLayout },
  archipelago: { theme: ARCHIPELAGO_THEME, generateLayout: generateFountainLayout },
  the_arch: { theme: THE_ARCH_THEME, generateLayout: generateColosseumLayout },
  twin_pillars: { theme: TWIN_PILLARS_THEME, generateLayout: generateTwinPeaksLayout },
  honeycomb: { theme: HONEYCOMB_THEME, generateLayout: generateThePitLayout },
  the_shelf: { theme: THE_SHELF_THEME, generateLayout: generateArchipelagoLayout },

  // 11 playable wide stages
  final_destination: FINAL_DESTINATION,
  battlefield: BATTLEFIELD,
  fountain: FOUNTAIN,
  stadium: STADIUM,
  skybridge: SKYBRIDGE,
  twin_peaks: TWIN_PEAKS,
  colosseum: COLOSSEUM,
  archipelago_stage: ARCHIPELAGO_STAGE,
  the_pit: THE_PIT,
  caldera: CALDERA,
  stalactite: STALACTITE,
};

const STAGE_ORDER = [
  // Row 1 — Starter / Competitive
  'final_destination', 'battlefield', 'fountain', 'stadium',
  // Row 2 — Technical / Varied
  'skybridge', 'twin_peaks', 'colosseum', 'archipelago_stage',
  // Row 3 — Aggressive / Creative + Random
  'the_pit', 'caldera', 'stalactite', 'random',
];

/** Get a stage definition by ID. Falls back to neon_void. */
export function getStage(id: string): StageDefinition {
  return STAGES[id] ?? { theme: NEON_VOID_THEME, generateLayout: generateFinalDestinationLayout };
}

/** Get theme by ID (without the layout generator). Falls back to neon_void. */
export function getThemeById(id: string): StageTheme {
  return (STAGES[id] ?? { theme: NEON_VOID_THEME }).theme;
}

// ── Custom theme override for custom levels ──────────────────────────

/** Module-level theme override — set when loading a custom level with theme overrides. */
let customThemeOverride: StageTheme | null = null;

/** Set a custom theme override (used by custom level loading). Pass null to clear. */
export function setCustomThemeOverride(theme: StageTheme | null): void {
  customThemeOverride = theme;
}

/** Get the theme for the active stage. Uses custom override if set. */
export function getActiveTheme(stageId: string): StageTheme {
  if (customThemeOverride) return customThemeOverride;
  return getStage(stageId).theme;
}

/** Get all stage IDs in display order. */
export function getAllStageIds(): string[] {
  return STAGE_ORDER;
}

/** Get all stage definitions in display order. */
export function getAllStages(): StageDefinition[] {
  return STAGE_ORDER.map(id => STAGES[id]);
}

/** Default stage ID. */
export const DEFAULT_STAGE_ID = 'final_destination';
